import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { staffClient } from 'clients/staff';
import { StaffApis } from 'apis/staff';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Select from 'ui/Select';
import Loading from 'ui/Loading';
import Table, { Column } from 'ui/Table';
import Modal from 'ui/Modal';
import Tabs from 'ui/Tabs';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import Section from 'ui/Section';
import TableContainer from 'ui/TableContainer';
import { Badge } from 'ui/shadcn';
import { cn } from 'lib/utils';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from 'utils/format';
import moment from 'moment';
import EducationManagement from './EducationManagement';

interface StaffData {
    id: number;
    user_id: string;
    name: string;
    user_type: string;
    password_reset_required: boolean;
    // 회원정보
    position?: string;
    phone_number?: string;
    birth_date?: string;
    join_date?: string;
    annual_leave_generated?: number;
    annual_leave_used?: number;
    previous_year_advance?: number;
    // 배치현장정보
    site_assignments?: Array<{
        id?: number;
        site_id: number;
        site?: {
            id: number;
            name: string;
            location?: string;
        };
        start_date: string;
        end_date?: string;
        is_reported: boolean;
        is_active: boolean;
        position: string;
    }>;
    // 교육 및 자격정보
    educations?: Array<{
        id?: number;
        supervisor_grade?: string;
        supervisor_certificate_number?: string;
        technical_qualification?: string;
        pq_education_date?: string;
        fire_safety_education_date?: string;
        display_order?: number;
    }>;
}

interface Site {
    id: number;
    name: string;
    location?: string;
    type?: string;
    constructionPeriod?: string;
}

const CAREER_FILTERS = [
    { value: 'ALL', label: '전체' },
    { value: 'ELECTRIC', label: '전기' },
    { value: 'FIRE', label: '소방' },
    { value: 'TELECOM', label: '통신' }
];

const StaffEdit: React.FC = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [staffData, setStaffData] = useState<StaffData | null>(null);
    const [formData, setFormData] = useState<Partial<StaffData>>({});
    const [saving, setSaving] = useState(false);

    // 모달 상태
    const [showSiteModal, setShowSiteModal] = useState(false);
    const [showCareerModal, setShowCareerModal] = useState(false);
    const [editingSiteAssignment, setEditingSiteAssignment] = useState<any>(null);
    const [editingCareer, setEditingCareer] = useState<any>(null);
    const [availableSites, setAvailableSites] = useState<Site[]>([]);
    const [qualifications, setQualifications] = useState<any[]>([]);
    const [careers, setCareers] = useState<any[]>([]);
    const [careerFilter, setCareerFilter] = useState('ALL');
    const [employments, setEmployments] = useState<any[]>([]);
    const [showEmploymentModal, setShowEmploymentModal] = useState(false);
    const [editingEmployment, setEditingEmployment] = useState<any>(null);
    const [employmentFormData, setEmploymentFormData] = useState({
        join_date: '',
        leave_date: '',
        note: ''
    });
    const [siteFormData, setSiteFormData] = useState({
        site_id: 0,
        start_date: '',
        end_date: '',
        is_reported: false,
        is_active: true,
        position: 'CHIEF'
    });
    const [careerFormData, setCareerFormData] = useState({
        career_type: 'ELECTRIC',
        project_name: '',
        start_date: '',
        end_date: '',
        evaluation_days: 0,
        conversion_rate: 0,
        converted_days: 0,
        participation_field: '',
        responsibilities: '',
        remarks: '',
        display_order: 0
    });

    const positionOptions = [
        { value: '대리', label: '대리' },
        { value: '과장', label: '과장' },
        { value: '차장', label: '차장' },
        { value: '부장', label: '부장' },
        { value: '이사', label: '이사' },
        { value: '상무', label: '상무' },
        { value: '고문', label: '고문' },
        { value: '부사장', label: '부사장' },
        { value: '대표이사', label: '대표이사' }
    ];

    const supervisorGradeOptions = [
        { value: '초급', label: '초급' },
        { value: '중급', label: '중급' },
        { value: '고급', label: '고급' },
        { value: '특급', label: '특급' }
    ];

    const qualificationOptions = [
        { value: '전기기술사', label: '전기기술사' },
        { value: '소방기술사', label: '소방기술사' },
        { value: '정보통신기술사', label: '정보통신기술사' },
        { value: '전기기사', label: '전기기사' },
        { value: '소방설비기사(기계)', label: '소방설비기사(기계)' },
        { value: '소방설비기사(전기)', label: '소방설비기사(전기)' },
        { value: '정보통신기사', label: '정보통신기사' }
    ];

    useEffect(() => {
        fetchStaffData();
        fetchAvailableSites();
        if (staffId) {
            fetchQualifications();
            fetchCareers();
            fetchEmployments();
        }
    }, [staffId]);

    useEffect(() => {
        if (staffId) {
            fetchCareers();
        }
    }, [careerFilter]);

    const fetchAvailableSites = async () => {
        try {
            const response = await staffClient.get('/staffs/sites/all');
            setAvailableSites(response.data);
        } catch (error) {
            console.error('Failed to fetch sites:', error);
        }
    };

    const fetchStaffData = async () => {
        try {
            const response = await staffClient.get(`/staffs/${staffId}`);
            console.log('Staff data:', response.data);

            // site_assignments 디버깅
            if (response.data.site_assignments) {
                console.log('Site assignments:', response.data.site_assignments);
                response.data.site_assignments.forEach((assignment: any, index: number) => {
                    console.log(`Assignment ${index}:`, assignment);
                    console.log(`Site data:`, assignment.site);
                    console.log(`Construction period:`, assignment.site?.constructionPeriod);
                });
            }

            setStaffData(response.data);
            setFormData(response.data);
        } catch (error) {
            console.error('Failed to fetch staff data:', error);
            toast.error('직원 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };


    const handleSave = async () => {
        setSaving(true);
        try {
            // 기본 정보 저장 (배치현장 제외)
            const { site_assignments, educations, ...basicData } = formData;
            await staffClient.patch(`/staffs/${staffId}`, basicData);

            toast.success('직원 정보가 저장되었습니다.');
            fetchStaffData();
        } catch (error) {
            console.error('Failed to save staff data:', error);
            toast.error('저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddSiteAssignment = async () => {
        try {
            if (staffId) {
                if (editingSiteAssignment) {
                    // 수정
                    await StaffApis.updateSiteAssignment(editingSiteAssignment.id, siteFormData);
                    toast.success('현장 배치가 수정되었습니다.');
                } else {
                    // 추가
                    await staffClient.post(`/staffs/${staffId}/assignments`, siteFormData);
                    toast.success('현장 배치가 추가되었습니다.');
                }

                await fetchStaffData();
            }

            setShowSiteModal(false);
            setEditingSiteAssignment(null);
            setSiteFormData({
                site_id: 0,
                start_date: '',
                end_date: '',
                is_reported: false,
                is_active: true,
                position: 'CHIEF'
            });
        } catch (error: any) {
            console.error('Failed to save site assignment:', error);
            const errorMessage = error.response?.data?.message || '현장 배치 저장에 실패했습니다.';
            toast.error(errorMessage);
        }
    };

    const handleEditSiteAssignment = (assignment: any) => {
        setEditingSiteAssignment(assignment);
        setSiteFormData({
            site_id: assignment.site_id,
            start_date: assignment.start_date ? moment(assignment.start_date).format('YYYY-MM-DD') : '',
            end_date: assignment.end_date ? moment(assignment.end_date).format('YYYY-MM-DD') : '',
            is_reported: assignment.is_reported || false,
            is_active: assignment.is_active !== false,
            position: assignment.position || 'CHIEF'
        });
        setShowSiteModal(true);
    };

    const handleDeleteSiteAssignment = async (assignment: any) => {
        if (window.confirm('이 배치현장 정보를 삭제하시겠습니까?')) {
            try {
                if (assignment.id) {
                    await staffClient.delete(`/staffs/assignments/${assignment.id}`);
                    await fetchStaffData();
                    toast.success('현장 배치가 삭제되었습니다.');
                }
            } catch (error) {
                console.error('Failed to delete site assignment:', error);
                toast.error('현장 배치 삭제에 실패했습니다.');
            }
        }
    };

    const fetchQualifications = async () => {
        try {
            const response = await StaffApis.getQualifications(Number(staffId));
            setQualifications(response);
        } catch (error) {
            console.error('Failed to fetch qualifications:', error);
        }
    };

    const fetchCareers = async () => {
        try {
            const response = await StaffApis.getCareers(Number(staffId), careerFilter);
            setCareers(response);
        } catch (error) {
            console.error('Failed to fetch careers:', error);
        }
    };

    // Employment (재직이력) handlers
    const fetchEmployments = async () => {
        try {
            const response = await staffClient.get(`/staffs/${staffId}/employments`);
            setEmployments(response.data);
        } catch (error) {
            console.error('Failed to fetch employments:', error);
        }
    };

    const handleAddEmployment = async () => {
        try {
            if (editingEmployment) {
                await staffClient.patch(`/staffs/employments/${editingEmployment.id}`, employmentFormData);
                toast.success('재직이력이 수정되었습니다.');
            } else {
                await staffClient.post(`/staffs/${staffId}/employments`, employmentFormData);
                toast.success('재직이력이 추가되었습니다.');
            }
            fetchEmployments();
            fetchStaffData();
            setShowEmploymentModal(false);
            setEditingEmployment(null);
            setEmploymentFormData({ join_date: '', leave_date: '', note: '' });
        } catch (error) {
            console.error('Failed to save employment:', error);
            toast.error('재직이력 저장에 실패했습니다.');
        }
    };

    const handleDeleteEmployment = async (employment: any) => {
        if (!window.confirm('이 재직이력을 삭제하시겠습니까?')) return;
        try {
            await staffClient.delete(`/staffs/employments/${employment.id}`);
            toast.success('재직이력이 삭제되었습니다.');
            fetchEmployments();
            fetchStaffData();
        } catch (error) {
            console.error('Failed to delete employment:', error);
            toast.error('재직이력 삭제에 실패했습니다.');
        }
    };

    // Qualification handlers
    const [editingQualification, setEditingQualification] = useState<any>(null);
    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [qualificationFormData, setQualificationFormData] = useState({
        supervisor_grade: '',
        supervisor_certificate_number: '',
        technical_qualification: '',
        acquisition_date: '',
        display_order: 0
    });

    const handleAddQualification = async () => {
        try {
            if (staffId) {
                if (editingQualification) {
                    // 수정
                    await StaffApis.updateQualification(editingQualification.id, qualificationFormData);
                    toast.success('자격 정보가 수정되었습니다.');
                } else {
                    // 추가
                    await StaffApis.addQualification(Number(staffId), qualificationFormData);
                    toast.success('자격 정보가 추가되었습니다.');
                }

                await fetchQualifications();
                setShowQualificationModal(false);
                setEditingQualification(null);
                setQualificationFormData({
                    supervisor_grade: '',
                    supervisor_certificate_number: '',
                    technical_qualification: '',
                    acquisition_date: '',
                    display_order: 0
                });
            }
        } catch (error) {
            console.error('Failed to save qualification:', error);
            toast.error('자격 정보 저장에 실패했습니다.');
        }
    };

    const handleEditQualification = (qualification: any) => {
        setEditingQualification(qualification);
        setQualificationFormData({
            supervisor_grade: qualification.supervisor_grade || '',
            supervisor_certificate_number: qualification.supervisor_certificate_number || '',
            technical_qualification: qualification.technical_qualification || '',
            acquisition_date: qualification.acquisition_date ? moment(qualification.acquisition_date).format('YYYY-MM-DD') : '',
            display_order: qualification.display_order || 0
        });
        setShowQualificationModal(true);
    };

    const handleDeleteQualification = async (qualification: any) => {
        if (window.confirm('이 자격 정보를 삭제하시겠습니까?')) {
            try {
                await StaffApis.removeQualification(qualification.id);
                await fetchQualifications();
                toast.success('자격 정보가 삭제되었습니다.');
            } catch (error) {
                console.error('Failed to delete qualification:', error);
                toast.error('자격 정보 삭제에 실패했습니다.');
            }
        }
    };

    const handleAddCareer = async () => {
        try {
            if (staffId) {
                if (editingCareer) {
                    // 수정
                    await StaffApis.updateCareer(editingCareer.id, careerFormData);
                    toast.success('경력 정보가 수정되었습니다.');
                } else {
                    // 추가
                    await StaffApis.addCareer(Number(staffId), careerFormData);
                    toast.success('경력 정보가 추가되었습니다.');
                }

                await fetchCareers();
                setShowCareerModal(false);
                setEditingCareer(null);
                setCareerFormData({
                    career_type: 'ELECTRIC',
                    project_name: '',
                    start_date: '',
                    end_date: '',
                    evaluation_days: 0,
                    conversion_rate: 0,
                    converted_days: 0,
                    participation_field: '',
                    responsibilities: '',
                    remarks: '',
                    display_order: 0
                });
            }
        } catch (error) {
            console.error('Failed to save career:', error);
            toast.error('경력 정보 저장에 실패했습니다.');
        }
    };

    const handleEditCareer = (career: any) => {
        setEditingCareer(career);
        setCareerFormData({
            career_type: career.career_type,
            project_name: career.project_name || '',
            start_date: career.start_date ? moment(career.start_date).format('YYYY-MM-DD') : '',
            end_date: career.end_date ? moment(career.end_date).format('YYYY-MM-DD') : '',
            evaluation_days: career.evaluation_days || 0,
            conversion_rate: career.conversion_rate || 0,
            converted_days: career.converted_days || 0,
            participation_field: career.participation_field || '',
            responsibilities: career.responsibilities || '',
            remarks: career.remarks || '',
            display_order: career.display_order || 0
        });
        setShowCareerModal(true);
    };

    const handleDeleteCareer = async (career: any) => {
        if (window.confirm('이 경력 정보를 삭제하시겠습니까?')) {
            try {
                await StaffApis.removeCareer(career.id);
                await fetchCareers();
                toast.success('경력 정보가 삭제되었습니다.');
            } catch (error) {
                console.error('Failed to delete career:', error);
                toast.error('경력 정보 삭제에 실패했습니다.');
            }
        }
    };

    const careerColumns: Column<any>[] = [
        {
            key: 'career_type',
            header: '구분',
            width: '80px',
            render: (value) => {
                const typeMap = {
                    'ELECTRIC': '전기',
                    'FIRE': '소방',
                    'TELECOM': '통신'
                };
                return typeMap[value] || value;
            }
        },
        {
            key: 'project_name',
            header: '사업명(용역명)',
            width: '200px',
            render: (value) => value || '-'
        },
        {
            key: 'period',
            header: '기간(참여기간)',
            width: '180px',
            render: (_, row) => {
                if (row.start_date || row.end_date) {
                    const start = row.start_date ? moment(row.start_date).format('YYYY-MM-DD') : '';
                    const end = row.end_date ? moment(row.end_date).format('YYYY-MM-DD') : '';
                    return <span className="whitespace-nowrap tabular-nums">{`${start} ~ ${end}`}</span>;
                }
                return '-';
            }
        },
        {
            key: 'evaluation_days',
            header: '평가환산일수',
            width: '100px',
            align: 'center',
            render: (value) => value || '-'
        },
        {
            key: 'conversion_rate',
            header: '환산비',
            width: '80px',
            align: 'center',
            render: (value) => value || '-'
        },
        {
            key: 'converted_days',
            header: '환산일수',
            width: '80px',
            align: 'center',
            render: (value) => value || '-'
        },
        {
            key: 'participation_field',
            header: '참여분야',
            width: '120px',
            render: (value) => value || '-'
        },
        {
            key: 'responsibilities',
            header: '담당업무',
            width: '150px',
            render: (value) => value || '-'
        },
        {
            key: 'remarks',
            header: '비고',
            width: '100px',
            render: (value) => value || '-'
        }
    ];

    const educationColumns: Column<any>[] = [
        {
            key: 'supervisor_grade',
            header: '감리등급',
            width: '120px',
            render: (value) => value || '-'
        },
        {
            key: 'supervisor_certificate_number',
            header: '감리원수첩번호',
            width: '180px',
            render: (value) => value || '-'
        },
        {
            key: 'technical_qualification',
            header: '기술자격',
            width: '200px',
            render: (value) => value || '-'
        },
        {
            key: 'acquisition_date',
            header: '취득일',
            width: '120px',
            render: (value) => value ? <span className="tabular-nums">{moment(value).format('YYYY-MM-DD')}</span> : '-'
        }
    ];

    const siteColumns: Column<any>[] = [
        {
            key: 'site',
            header: '현장명',
            width: '250px',
            render: (value) => value ? (
                <button
                    type="button"
                    className="text-left font-medium text-primary hover:underline"
                    onClick={() => navigate(`/admin/site-management/${value.id}/edit?tab=supervisors`)}
                >
                    {value.name}
                </button>
            ) : '-'
        },
        {
            key: 'position',
            header: '구분',
            width: '100px',
            align: 'center',
            render: (value) => {
                const positionMap: Record<string, string> = {
                    'CHIEF': '책임감리',
                    'ASSISTANT': '보조감리',
                    'NON_RESIDENT': '비상주',
                    'ETC': '기타'
                };
                return positionMap[value] || '-';
            }
        },
        {
            key: 'start_date',
            header: '시작일',
            width: '120px',
            render: (value) => value ? <span className="tabular-nums">{new Date(value).toLocaleDateString('ko-KR')}</span> : '-'
        },
        {
            key: 'end_date',
            header: '종료일',
            width: '120px',
            render: (value) => value ? <span className="tabular-nums">{new Date(value).toLocaleDateString('ko-KR')}</span> : '-'
        },
        {
            key: 'is_active',
            header: '상태',
            width: '80px',
            align: 'center',
            render: (value) => (
                <Badge variant={value !== false ? 'success' : 'secondary'}>
                    {value !== false ? '활성' : '비활성'}
                </Badge>
            )
        }
    ];

    const employmentColumns: Column<any>[] = [
        {
            key: 'join_date',
            header: '입사일',
            width: '140px',
            render: (value: string) => value ? <span className="tabular-nums">{moment(value).format('YYYY.MM.DD')}</span> : '-'
        },
        {
            key: 'leave_date',
            header: '퇴사일',
            width: '140px',
            render: (value: string) => value ? <span className="tabular-nums">{moment(value).format('YYYY.MM.DD')}</span> : (
                <Badge variant="success">재직중</Badge>
            )
        },
        {
            key: 'note',
            header: '비고',
            render: (value: string) => value || '-'
        }
    ];

    const renderRowActions = (onEdit: () => void, onDelete: () => void) => (
        <>
            <Button size="small" variant="outline" onClick={onEdit}>
                수정
            </Button>
            <Button size="small" variant="danger-outline" onClick={onDelete}>
                삭제
            </Button>
        </>
    );

    const closeSiteModal = () => {
        setShowSiteModal(false);
        setEditingSiteAssignment(null);
        setSiteFormData({
            site_id: 0,
            start_date: '',
            end_date: '',
            is_reported: false,
            is_active: true,
            position: 'CHIEF'
        });
    };

    if (loading) {
        return (
            <PageContainer>
                <Loading text="직원 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    if (!staffData) {
        return (
            <PageContainer>
                <Section>
                    <p className="py-12 text-center text-sm text-muted-foreground">
                        직원 정보를 찾을 수 없습니다.
                    </p>
                </Section>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="직원 정보 수정"
                subtitle={
                    <span className="inline-flex items-center gap-2">
                        <span>{staffData.name} ({staffData.user_id})</span>
                        {staffData.user_type === 'MASTER' && (
                            <Badge variant="default">관리자</Badge>
                        )}
                    </span>
                }
                breadcrumb={[
                    { label: '관리자', path: '/main/home' },
                    { label: '직원 관리', path: '/admin/staffs' },
                    { label: '직원 정보 수정' }
                ]}
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate('/admin/staffs')}
                        >
                            취소
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? '저장 중...' : '저장'}
                        </Button>
                    </div>
                }
            />

            {/* Tabs Navigation and Content */}
            <Tabs
                tabs={[
                    {
                        id: 'basic',
                        label: '기본 정보',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        ),
                        content: (
                            <div className="space-y-4">
                                <Section title="기본 정보">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                        <Input
                                            label="사용자 ID"
                                            value={formData.user_id || ''}
                                            disabled
                                        />
                                        <Input
                                            label="이름"
                                            value={formData.name || ''}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        />
                                        <Select
                                            label="직급"
                                            value={formData.position || ''}
                                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                            options={positionOptions}
                                        />
                                        <Input
                                            label="휴대폰번호"
                                            value={formData.phone_number || ''}
                                            onChange={(e) => setFormData({ ...formData, phone_number: formatPhoneNumber(e.target.value) })}
                                            placeholder="010-1234-5678"
                                        />
                                        <Input
                                            label="생년월일"
                                            type="date"
                                            value={formData.birth_date || ''}
                                            onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                                        />
                                    </div>
                                </Section>

                                {/* 재직이력 */}
                                <TableContainer
                                    title="재직이력"
                                    resultCount={employments.length}
                                    actions={
                                        <Button
                                            variant="outline"
                                            size="small"
                                            icon={<Plus />}
                                            onClick={() => {
                                                setEditingEmployment(null);
                                                setEmploymentFormData({ join_date: '', leave_date: '', note: '' });
                                                setShowEmploymentModal(true);
                                            }}
                                        >
                                            추가
                                        </Button>
                                    }
                                >
                                    <Table
                                        columns={employmentColumns}
                                        data={employments}
                                        actionsColumnWidth="140px"
                                        actions={(row: any) =>
                                            renderRowActions(
                                                () => {
                                                    setEditingEmployment(row);
                                                    setEmploymentFormData({
                                                        join_date: row.join_date ? moment(row.join_date).format('YYYY-MM-DD') : '',
                                                        leave_date: row.leave_date ? moment(row.leave_date).format('YYYY-MM-DD') : '',
                                                        note: row.note || ''
                                                    });
                                                    setShowEmploymentModal(true);
                                                },
                                                () => handleDeleteEmployment(row)
                                            )
                                        }
                                        emptyMessage="재직이력이 없습니다. 상단의 '+ 추가' 버튼으로 등록해주세요."
                                    />
                                </TableContainer>
                            </div>
                        )
                    },
                    {
                        id: 'sites',
                        label: '배치현장',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        ),
                        content: (
                            <TableContainer
                                title="배치 현장 목록"
                                resultCount={formData.site_assignments?.length || 0}
                            >
                                <Table
                                    columns={siteColumns}
                                    data={formData.site_assignments || []}
                                    emptyMessage="배치된 현장이 없습니다."
                                />
                            </TableContainer>
                        )
                    },
                    {
                        id: 'education',
                        label: '교육 관리',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        ),
                        content: (
                            <EducationManagement staffId={Number(staffId)} />
                        )
                    },
                    {
                        id: 'qualification',
                        label: '자격 관리',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14v7" />
                            </svg>
                        ),
                        content: (
                            <TableContainer
                                title="자격 목록"
                                resultCount={qualifications?.length || 0}
                                actions={
                                    <Button
                                        variant="outline"
                                        size="small"
                                        icon={<Plus />}
                                        onClick={() => {
                                            setEditingQualification(null);
                                            setQualificationFormData({
                                                supervisor_grade: '',
                                                supervisor_certificate_number: '',
                                                technical_qualification: '',
                                                acquisition_date: '',
                                                display_order: 0
                                            });
                                            setShowQualificationModal(true);
                                        }}
                                    >
                                        자격 추가
                                    </Button>
                                }
                            >
                                <Table
                                    columns={educationColumns}
                                    data={qualifications || []}
                                    actionsColumnWidth="140px"
                                    actions={(row: any) =>
                                        renderRowActions(
                                            () => handleEditQualification(row),
                                            () => handleDeleteQualification(row)
                                        )
                                    }
                                    emptyMessage="등록된 자격 정보가 없습니다. 상단의 '자격 추가' 버튼을 클릭하여 정보를 추가해주세요."
                                />
                            </TableContainer>
                        )
                    },
                    {
                        id: 'career',
                        label: '경력 관리',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        ),
                        content: (
                            <TableContainer
                                title="경력 목록"
                                resultCount={careers?.length || 0}
                                actions={
                                    <>
                                        <div className="flex items-center gap-1">
                                            {CAREER_FILTERS.map((filter) => (
                                                <button
                                                    key={filter.value}
                                                    type="button"
                                                    onClick={() => setCareerFilter(filter.value)}
                                                    className={cn(
                                                        'inline-flex h-7 items-center rounded px-2.5 text-sm font-medium transition-colors duration-fast',
                                                        careerFilter === filter.value
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'text-neutral-700 hover:bg-muted hover:text-foreground'
                                                    )}
                                                >
                                                    {filter.label}
                                                </button>
                                            ))}
                                        </div>
                                        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
                                        <Button
                                            variant="outline"
                                            size="small"
                                            icon={<Plus />}
                                            onClick={() => {
                                                setEditingCareer(null);
                                                setCareerFormData({
                                                    career_type: 'ELECTRIC',
                                                    project_name: '',
                                                    start_date: '',
                                                    end_date: '',
                                                    evaluation_days: 0,
                                                    conversion_rate: 0,
                                                    converted_days: 0,
                                                    participation_field: '',
                                                    responsibilities: '',
                                                    remarks: '',
                                                    display_order: 0
                                                });
                                                setShowCareerModal(true);
                                            }}
                                        >
                                            경력 추가
                                        </Button>
                                    </>
                                }
                            >
                                <Table
                                    columns={careerColumns}
                                    data={careers || []}
                                    actionsColumnWidth="140px"
                                    actions={(row: any) =>
                                        renderRowActions(
                                            () => handleEditCareer(row),
                                            () => handleDeleteCareer(row)
                                        )
                                    }
                                    emptyMessage="등록된 경력 정보가 없습니다. 상단의 '경력 추가' 버튼을 클릭하여 정보를 추가해주세요."
                                />
                            </TableContainer>
                        )
                    }
                ]}
                defaultTab="basic"
                variant="default"
            />

            {/* 현장 추가 모달 */}
            <Modal
                isOpen={showSiteModal}
                onClose={closeSiteModal}
                title={editingSiteAssignment ? "현장 배치 수정" : "현장 배치 추가"}
                footer={
                    <>
                        <Button variant="outline" onClick={closeSiteModal}>취소</Button>
                        <Button onClick={handleAddSiteAssignment}>{editingSiteAssignment ? '수정' : '추가'}</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="현장 선택"
                        value={siteFormData.site_id}
                        onChange={(e) => setSiteFormData({ ...siteFormData, site_id: Number(e.target.value) })}
                        options={[
                            { value: 0, label: '현장을 선택하세요' },
                            ...availableSites.map(site => ({
                                value: site.id,
                                label: `${site.name}${site.location ? ` (${site.location})` : ''}`
                            }))
                        ]}
                    />
                    <Select
                        label="구분"
                        value={siteFormData.position}
                        onChange={(e) => setSiteFormData({ ...siteFormData, position: e.target.value })}
                        options={[
                            { value: 'CHIEF', label: '책임감리' },
                            { value: 'ASSISTANT', label: '보조감리' },
                            { value: 'NON_RESIDENT', label: '비상주' },
                            { value: 'ETC', label: '기타' }
                        ]}
                    />
                    <Input
                        label="시작일"
                        type="date"
                        value={siteFormData.start_date}
                        onChange={(e) => setSiteFormData({ ...siteFormData, start_date: e.target.value })}
                    />
                    <Input
                        label="종료일"
                        type="date"
                        value={siteFormData.end_date}
                        onChange={(e) => setSiteFormData({ ...siteFormData, end_date: e.target.value })}
                    />
                    <Select
                        label="상태"
                        value={siteFormData.is_active ? 'true' : 'false'}
                        onChange={(e) => setSiteFormData({ ...siteFormData, is_active: e.target.value === 'true' })}
                        options={[
                            { value: 'true', label: '활성' },
                            { value: 'false', label: '비활성' }
                        ]}
                    />
                </div>
            </Modal>

            {/* 자격 추가/수정 모달 */}
            <Modal
                isOpen={showQualificationModal}
                onClose={() => {
                    setShowQualificationModal(false);
                    setEditingQualification(null);
                }}
                title={editingQualification ? "자격 정보 수정" : "자격 정보 추가"}
                footer={
                    <>
                        <Button variant="outline" onClick={() => {
                            setShowQualificationModal(false);
                            setEditingQualification(null);
                        }}>취소</Button>
                        <Button onClick={handleAddQualification}>{editingQualification ? '수정' : '추가'}</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="감리등급"
                        value={qualificationFormData.supervisor_grade}
                        onChange={(e) => setQualificationFormData({ ...qualificationFormData, supervisor_grade: e.target.value })}
                        options={supervisorGradeOptions}
                    />
                    <Input
                        label="감리원수첩번호"
                        type="text"
                        value={qualificationFormData.supervisor_certificate_number}
                        onChange={(e) => setQualificationFormData({ ...qualificationFormData, supervisor_certificate_number: e.target.value })}
                        placeholder="예: 10-118376 (전기)"
                    />
                    <Select
                        label="기술자격"
                        value={qualificationFormData.technical_qualification}
                        onChange={(e) => setQualificationFormData({ ...qualificationFormData, technical_qualification: e.target.value })}
                        options={qualificationOptions}
                    />
                    <Input
                        label="취득일"
                        type="date"
                        value={qualificationFormData.acquisition_date}
                        onChange={(e) => setQualificationFormData({ ...qualificationFormData, acquisition_date: e.target.value })}
                    />
                </div>
            </Modal>

            {/* 경력 추가/수정 모달 */}
            <Modal
                isOpen={showCareerModal}
                onClose={() => {
                    setShowCareerModal(false);
                    setEditingCareer(null);
                }}
                title={editingCareer ? "경력 정보 수정" : "경력 정보 추가"}
                footer={
                    <>
                        <Button variant="outline" onClick={() => {
                            setShowCareerModal(false);
                            setEditingCareer(null);
                        }}>취소</Button>
                        <Button onClick={handleAddCareer}>{editingCareer ? '수정' : '추가'}</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="구분"
                        value={careerFormData.career_type}
                        onChange={(e) => setCareerFormData({ ...careerFormData, career_type: e.target.value })}
                        options={[
                            { value: 'ELECTRIC', label: '전기' },
                            { value: 'FIRE', label: '소방' },
                            { value: 'TELECOM', label: '통신' }
                        ]}
                    />
                    <Input
                        label="사업명(용역명)"
                        type="text"
                        value={careerFormData.project_name}
                        onChange={(e) => setCareerFormData({ ...careerFormData, project_name: e.target.value })}
                        placeholder="사업명 또는 용역명 입력"
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Input
                            label="참여 시작일"
                            type="date"
                            value={careerFormData.start_date}
                            onChange={(e) => setCareerFormData({ ...careerFormData, start_date: e.target.value })}
                        />
                        <Input
                            label="참여 종료일"
                            type="date"
                            value={careerFormData.end_date}
                            onChange={(e) => setCareerFormData({ ...careerFormData, end_date: e.target.value })}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <Input
                            label="평가환산일수"
                            type="number"
                            value={careerFormData.evaluation_days}
                            onChange={(e) => setCareerFormData({ ...careerFormData, evaluation_days: Number(e.target.value) })}
                        />
                        <Input
                            label="환산비"
                            type="number"
                            step="0.01"
                            value={careerFormData.conversion_rate}
                            onChange={(e) => setCareerFormData({ ...careerFormData, conversion_rate: Number(e.target.value) })}
                        />
                        <Input
                            label="환산일수"
                            type="number"
                            value={careerFormData.converted_days}
                            onChange={(e) => setCareerFormData({ ...careerFormData, converted_days: Number(e.target.value) })}
                        />
                    </div>
                    <Input
                        label="참여분야"
                        type="text"
                        value={careerFormData.participation_field}
                        onChange={(e) => setCareerFormData({ ...careerFormData, participation_field: e.target.value })}
                        placeholder="참여분야 입력"
                    />
                    <Input
                        label="담당업무"
                        type="text"
                        value={careerFormData.responsibilities}
                        onChange={(e) => setCareerFormData({ ...careerFormData, responsibilities: e.target.value })}
                        placeholder="담당업무 입력"
                    />
                    <Input
                        label="비고"
                        type="text"
                        value={careerFormData.remarks}
                        onChange={(e) => setCareerFormData({ ...careerFormData, remarks: e.target.value })}
                        placeholder="비고 입력"
                    />
                </div>
            </Modal>

            {/* Employment Modal */}
            <Modal
                isOpen={showEmploymentModal}
                onClose={() => {
                    setShowEmploymentModal(false);
                    setEditingEmployment(null);
                }}
                title={editingEmployment ? '재직이력 수정' : '재직이력 추가'}
                footer={
                    <>
                        <Button variant="outline" onClick={() => {
                            setShowEmploymentModal(false);
                            setEditingEmployment(null);
                        }}>취소</Button>
                        <Button onClick={handleAddEmployment} disabled={!employmentFormData.join_date}>
                            {editingEmployment ? '수정' : '추가'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="입사일"
                        required
                        type="date"
                        value={employmentFormData.join_date}
                        onChange={(e) => setEmploymentFormData({ ...employmentFormData, join_date: e.target.value })}
                    />
                    <div>
                        <Input
                            label="퇴사일"
                            type="date"
                            value={employmentFormData.leave_date}
                            onChange={(e) => setEmploymentFormData({ ...employmentFormData, leave_date: e.target.value })}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">비워두면 재직중으로 표시됩니다.</p>
                    </div>
                    <Input
                        label="비고"
                        type="text"
                        value={employmentFormData.note}
                        onChange={(e) => setEmploymentFormData({ ...employmentFormData, note: e.target.value })}
                        placeholder="비고 입력"
                    />
                </div>
            </Modal>

        </PageContainer>
    );
};

export default StaffEdit;
